import { Flex, Image, Loader } from '@mantine/core';
import { TextComponent } from '@manifest-cyber/design-system/lib/components/TextComponent';
import { MRT_ColumnDef, MRT_Table, useMantineReactTable } from 'mantine-react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AtomLightIcon } from '../Icons/AtomLight/AtomLightIcon';
import ClickableRegion from '../ClickableRegion/ClickableRegion';
import { GetAiModelSearchHistoryResponseData } from '@manifest-cyber/types/interface/contracts/model-analysis';
import { RiskScoreColumn } from './RiskScoreColumn';

type GetAiModelSearchHistoryResponseItem = GetAiModelSearchHistoryResponseData[number];

export type RecentModelSearchesTableProps = {
  data: GetAiModelSearchHistoryResponseData;
  isLoading: boolean;
  isFetching?: boolean;
  handleClear: () => void;
  showClearButton?: boolean;
};

type ModelNameCellProps = Pick<
  GetAiModelSearchHistoryResponseItem,
  '_id' | 'name' | 'supplier' | 'supplierLogoUrl' | 'status'
>;

const ModelNameCell = ({
  model: { _id: modelId, name, supplier, supplierLogoUrl, status },
}: {
  model: ModelNameCellProps;
}) => {
  const logoSize = 40;
  const logoContainerSize = 50;
  const modelImageCandidate = supplierLogoUrl ? (
    <Image src={supplierLogoUrl} alt={supplier} height={logoSize} width={logoSize} />
  ) : (
    <AtomLightIcon width={logoSize} height={logoSize} />
  );

  const isRunning = status === 'running';
  const modelImage = isRunning ? <Loader size={18} /> : modelImageCandidate;
  const hrefLink = modelId
    ? `/ai-explorer/model/${encodeURIComponent(modelId.toString())}`
    : '#';

  return (
    <Flex direction="row" gap="xs" align="center">
      <Flex align="center" justify="center" w={logoContainerSize} miw={logoContainerSize}>
        {modelImage}
      </Flex>
      <Flex direction="column" gap={4}>
        <TextComponent variant="componentSmall" color="muted">
          {supplier.toUpperCase()}
        </TextComponent>
        <ClickableRegion href={hrefLink}>
          <TextComponent variant="headerMedium" color="title">
            {name}
          </TextComponent>
        </ClickableRegion>
      </Flex>
    </Flex>
  );
};

export const RecentModelSearchesTable = ({
  data,
  isLoading,
  handleClear,
  showClearButton = true,
}: RecentModelSearchesTableProps) => {
  const { t } = useTranslation();

  const columns = useMemo<MRT_ColumnDef<GetAiModelSearchHistoryResponseItem>[]>(
    () => [
      {
        header: t('page.ai-explore.recentSearchesTable.modelName'),
        Cell: ({ cell }) => {
          return <ModelNameCell model={cell.row.original} />;
        },
      },
      {
        header: t('page.ai-explore.recentSearchesTable.modelRisk'),
        Cell: ({ cell }) => {
          return <RiskScoreColumn cell={cell} row={cell.row} />;
        },
      },
      {
        accessorKey: 'modelTaskType',
        header: t('page.ai-explore.recentSearchesTable.modelTaskType'),
        Cell: ({ cell }) => {
          const { type } = cell.row.original;
          return <TextComponent variant="bodyMedium">{type}</TextComponent>;
        },
      },
      {
        header: t('page.ai-explore.recentSearchesTable.modelLicense'),
        Cell: ({ cell }) => {
          const licenses = cell.row.original?.licenses?.join(', ');
          return <TextComponent variant="bodyMedium">{licenses}</TextComponent>;
        },
      },
      {
        header: t('page.ai-explore.recentSearchesTable.modelScanDate'),
        Cell: ({ cell }) => {
          const { dateCreated } = cell.row.original;
          const dateModifiedString = dateCreated
            ? new Date(dateCreated).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })
            : '';
          return <TextComponent variant="bodyMedium">{dateModifiedString}</TextComponent>;
        },
      },
    ],
    [],
  );

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnOrdering: false,
    enableColumnPinning: false,
    enableColumnResizing: false,
    enableSorting: false,
    enableColumnActions: false,
    enablePagination: false,
    enableHiding: false,
    enablePinning: false,
    state: {
      isLoading,
    },
  });

  return (
    <Flex direction="column" gap="xs" w="100%">
      <Flex direction="row" align="center" gap="sm">
        <TextComponent variant="headerLarge">
          {t('page.ai-explore.recentModels')}
        </TextComponent>
        {showClearButton && (
          <ClickableRegion onClick={() => handleClear()} href={'#'}>
            <TextComponent variant="bodyMedium" color="primary">
              {t('page.ai-explore.clear')}
            </TextComponent>
          </ClickableRegion>
        )}
      </Flex>
      <MRT_Table table={table} />
    </Flex>
  );
};
